import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { AlertCircle, AlertTriangle, Ban, CheckCircle, Clock, Loader2, type LucideIcon } from "lucide-react";
import type { CSSProperties, PropsWithChildren } from "react";
import { FormattedTime } from "@defuse/ui/formatted-time";
import { Price } from "@defuse/ui/price";
import { useTokens } from "@defuse/ui/tokens-provider";
import { RecipientValue } from "@/components/recipient";
import { getAddressLink, getTxLink, Short } from "@/components/short";
import { REFUND_REASON_MAP } from "@intents-explorer/shared-api/refund-reasons";
import { shouldShowWithdrawTransactions } from "@intents-explorer/shared-api/transactions/transactionHashes";
import type { OneClickDetails, TransactionDetails } from "@/lib/transactions/api/getTransactionByDepositAddress";
import { cn } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import { SubmitDepositTxForm } from "./submit-deposit-tx-form";
import { getDepositChain, getRecipientChain, getRefundChain } from "@/lib/transactions/get-transaction-chain";
import { SwapStepTimestamp } from "./swap-step-timestamp";
import { SwapSteps } from "./swap-steps";
const PRICE_LENGTH = 10;
const HASH_LENGTH = 15;
function formatRefundReason(reason: string | null | undefined): string | null {
  if (reason === null || reason === undefined || reason === "") {
    return null;
  }
  const mappedReason = REFUND_REASON_MAP[reason];
  if (mappedReason !== undefined) {
    return mappedReason;
  }
  const formatted = reason.split("_").join(" ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
}
type StepItemType = "pending" | "warning" | "success" | "processing" | "failed" | "refunded";
const stepItemTitleVariants = cva("pt-1 font-medium", {
  variants: {
    type: {
      pending: "text-yellow-800 dark:text-yellow-100",
      warning: "text-yellow-800 dark:text-yellow-100",
      success: "text-green-900 dark:text-green-100",
      processing: "text-blue-900 dark:text-blue-100",
      failed: "text-red-900 dark:text-red-100",
      refunded: "text-purple-900 dark:text-purple-100",
      unknown: "text-gray-900 dark:text-gray-100"
    }
  }
});
const stepItemDescriptionVariants = cva("text-sm", {
  variants: {
    type: {
      pending: "text-yellow-700 dark:text-yellow-300",
      warning: "text-yellow-700 dark:text-yellow-300",
      success: "text-green-700 dark:text-green-300",
      processing: "text-blue-700 dark:text-blue-300",
      failed: "text-red-700 dark:text-red-300",
      refunded: "text-purple-700 dark:text-purple-300",
      unknown: "text-gray-700 dark:text-gray-300"
    }
  }
});
const stepItemIconVariants = cva("h-5 w-5", {
  variants: {
    type: {
      pending: "text-yellow-600 dark:text-yellow-400",
      warning: "text-yellow-600 dark:text-yellow-400",
      success: "text-green-500 dark:text-green-400",
      processing: "animate-spin text-blue-500 dark:text-blue-400",
      failed: "text-red-500 dark:text-red-400",
      refunded: "text-purple-500 dark:text-purple-400",
      unknown: "text-gray-500 dark:text-gray-400"
    }
  }
});
const typeToIcon: Record<StepItemType, LucideIcon> = {
  pending: Clock,
  warning: AlertTriangle,
  success: CheckCircle,
  processing: Loader2,
  failed: AlertCircle,
  refunded: Ban
};
const typeToLightColor: Record<StepItemType, string> = {
  pending: "color-mix(in oklab,var(--color-yellow-50)50%,transparent)",
  warning: "color-mix(in oklab,var(--color-yellow-50)50%,transparent)",
  success: "color-mix(in oklab,var(--color-green-50)50%,transparent)",
  processing: "color-mix(in oklab,var(--color-blue-50)50%,transparent)",
  failed: "color-mix(in oklab,var(--color-red-50)50%,transparent)",
  refunded: "color-mix(in oklab,var(--color-purple-50)50%,transparent)"
};
const typeToDarkColor: Record<StepItemType, string> = {
  pending: "color-mix(in oklab,var(--color-yellow-950)15%,transparent)",
  warning: "color-mix(in oklab,var(--color-yellow-950)15%,transparent)",
  success: "color-mix(in oklab, var(--color-green-950)15%, transparent)",
  processing: "color-mix(in oklab,var(--color-blue-950)15%,transparent)",
  failed: "color-mix(in oklab,var(--color-red-950)10%,transparent)",
  refunded: "color-mix(in oklab,var(--color-purple-950)10%,transparent)"
};
function getGradientStyles(type: StepItemType, prevType: StepItemType, nextType: StepItemType): CSSProperties & {
  "--step-gradient-light"?: string;
  "--step-gradient-dark"?: string;
} {
  const currentLight = typeToLightColor[type];
  const currentDark = typeToDarkColor[type];
  const prevLight = typeToLightColor[prevType];
  const prevDark = typeToDarkColor[prevType];
  const nextLight = typeToLightColor[nextType];
  const nextDark = typeToDarkColor[nextType];
  const lightMixStart = `color-mix(in oklab, ${prevLight} 50%, ${currentLight})`;
  const lightMixEnd = `color-mix(in oklab, ${currentLight} 50%, ${nextLight})`;
  const darkMixStart = `color-mix(in oklab, ${prevDark} 50%, ${currentDark})`;
  const darkMixEnd = `color-mix(in oklab, ${currentDark} 50%, ${nextDark})`;
  return {
    "--step-gradient-light": `linear-gradient(to bottom, ${lightMixStart} 0%, ${currentLight} 15%, ${currentLight} 85%, ${lightMixEnd} 100%)`,
    "--step-gradient-dark": `linear-gradient(to bottom, ${darkMixStart} 0%, ${currentDark} 15%, ${currentDark} 85%, ${darkMixEnd} 100%)`
  };
}
interface StepItemProps extends PropsWithChildren {
  title: string;
  type: StepItemType;
  description?: string;
  isLast?: boolean;
  isFirst?: boolean;
  prevType?: StepItemType;
  nextType?: StepItemType;
}
function StepItem(t0) {
  const $ = _c(36);
  const {
    title,
    description,
    isLast: t1,
    isFirst: t2,
    children,
    type,
    prevType: t3,
    nextType: t4
  } = t0;
  const isLast = t1 === undefined ? false : t1;
  const isFirst = t2 === undefined ? false : t2;
  const prevType = t3 === undefined ? type : t3;
  const nextType = t4 === undefined ? type : t4;
  const Icon = typeToIcon[type];
  let t5;
  if ($[0] !== nextType || $[1] !== prevType || $[2] !== type) {
    t5 = getGradientStyles(type, prevType, nextType);
    $[0] = nextType;
    $[1] = prevType;
    $[2] = type;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const gradientStyles = t5;
  const t6 = isLast && "pb-8 md:rounded-b-lg";
  const t7 = isFirst && "md:rounded-t-lg";
  let t8;
  if ($[4] !== t6 || $[5] !== t7) {
    t8 = cn("step-item-gradient relative flex flex-1 flex-col gap-3 transition-all duration-200", t6, t7);
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== isLast) {
    t9 = !isLast && <span className="absolute top-[50px] -bottom-3 left-6 z-10 w-2 -translate-x-1/2 transform rounded-full bg-black/10 sm:left-8 dark:bg-white/10" />;
    $[7] = isLast;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== type) {
    t10 = stepItemIconVariants({
      type
    });
    $[9] = type;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== Icon || $[12] !== t10) {
    t11 = <div className="flex h-8 w-8 items-center justify-center"><Icon className={t10} /></div>;
    $[11] = Icon;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== type) {
    t12 = stepItemTitleVariants({
      type
    });
    $[14] = type;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== title) {
    t13 = <h3 className={t12}>{title}</h3>;
    $[16] = t12;
    $[17] = title;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t11 || $[20] !== t13) {
    t14 = <div className="flex flex-wrap items-center justify-between gap-3 px-2 pt-4 sm:px-4"><div className="flex w-full items-start gap-2">{t11}{t13}</div></div>;
    $[19] = t11;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== description || $[23] !== type) {
    t15 = description !== undefined && description !== "" && <p className={stepItemDescriptionVariants({
      type
    })}>{description}</p>;
    $[22] = description;
    $[23] = type;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== children || $[26] !== t15) {
    t16 = <div className="flex flex-col gap-y-3 pr-4 pl-12 sm:pl-14">{t15}{children}</div>;
    $[25] = children;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t14 || $[29] !== t16) {
    t17 = <div className="relative z-20 flex flex-col gap-y-3">{t14}{t16}</div>;
    $[28] = t14;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== gradientStyles || $[32] !== t17 || $[33] !== t8 || $[34] !== t9) {
    t18 = <div className="relative flex"><div className={t8} style={gradientStyles}>{t9}{t17}</div></div>;
    $[31] = gradientStyles;
    $[32] = t17;
    $[33] = t8;
    $[34] = t9;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
}
function PendingDeposit(t0) {
  const $ = _c(20);
  const {
    quote,
    originChain,
    prevType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "pending" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== originChain || $[3] !== quote.depositAddress || $[4] !== quote.depositType) {
    t2 = <DepositAddressDisplay depositAddress={quote.depositAddress} depositType={quote.depositType} originChain={originChain} />;
    $[2] = originChain;
    $[3] = quote.depositAddress;
    $[4] = quote.depositType;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== quote.depositMemo) {
    t3 = quote.depositMemo !== undefined && quote.depositMemo !== "" && <>with memo{" "}<Short text={quote.depositMemo} length={HASH_LENGTH} />{" "}</>;
    $[6] = quote.depositMemo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== quote.requestDeadline) {
    t4 = <FormattedTime date={quote.requestDeadline} />;
    $[8] = quote.requestDeadline;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <p className="text-sm">Make sure funds were deposited to{" "}{t2}{" "}{t3}until the deadline {t4}</p>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== quote.depositAddress) {
    t6 = <SubmitDepositTxForm depositAddress={quote.depositAddress} />;
    $[14] = quote.depositAddress;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t1 || $[17] !== t5 || $[18] !== t6) {
    t7 = <StepItem title="Pending Deposit" description="Waiting for token deposit confirmation. Amounts are hidden on this page for security reasons." isLast={true} type="pending" {...t1}>{t5}{t6}</StepItem>;
    $[16] = t1;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function PartialDeposit(t0) {
  const $ = _c(22);
  const {
    quote,
    originChain,
    nextType
  } = t0;
  let t1;
  if ($[0] !== nextType) {
    t1 = nextType !== undefined && nextType !== "success" && {
      nextType
    };
    $[0] = nextType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== originChain || $[3] !== quote.depositAddress || $[4] !== quote.depositType) {
    t2 = <DepositAddressDisplay depositAddress={quote.depositAddress} depositType={quote.depositType} originChain={originChain} />;
    $[2] = originChain;
    $[3] = quote.depositAddress;
    $[4] = quote.depositType;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== quote.depositMemo) {
    t3 = quote.depositMemo !== undefined && quote.depositMemo !== "" && <>with memo{" "}<Short text={quote.depositMemo} length={HASH_LENGTH} />{" "}</>;
    $[6] = quote.depositMemo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== quote) {
    t4 = <SwapStepTimestamp quote={quote} />;
    $[8] = quote;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <p className="flex flex-wrap gap-x-2 text-sm leading-7">Tokens were partially deposited to{" "}{t2}{" "}{t3}{t4}</p>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== originChain || $[15] !== quote.depositTransactions || $[16] !== quote.depositType) {
    t6 = quote.depositTransactions.length > 0 && <p className="flex flex-wrap gap-x-2 text-sm">Deposit {pluralize(quote.depositTransactions.length, "transaction")}:{" "}{quote.depositTransactions.map(tx => <Short key={tx.txHash} text={tx.txHash} length={HASH_LENGTH} link={getTxLink(getDepositChain(quote.depositType, originChain), tx.txHash)} />)}</p>;
    $[14] = originChain;
    $[15] = quote.depositTransactions;
    $[16] = quote.depositType;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t1 || $[19] !== t5 || $[20] !== t6) {
    t7 = <StepItem title="Partial Deposit" description="Partial token deposit has been confirmed." type="success" {...t1}>{t5}{t6}</StepItem>;
    $[18] = t1;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function IncompleteDeposit(t0) {
  const $ = _c(20);
  const {
    quote,
    originChain,
    prevType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "warning" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== originChain || $[3] !== quote.depositAddress || $[4] !== quote.depositType) {
    t2 = <DepositAddressDisplay depositAddress={quote.depositAddress} depositType={quote.depositType} originChain={originChain} />;
    $[2] = originChain;
    $[3] = quote.depositAddress;
    $[4] = quote.depositType;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== quote.depositMemo) {
    t3 = quote.depositMemo !== undefined && quote.depositMemo !== "" && <>{" "}with memo <Short text={quote.depositMemo} length={HASH_LENGTH} /></>;
    $[6] = quote.depositMemo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== quote.requestDeadline) {
    t4 = <FormattedTime date={quote.requestDeadline} />;
    $[8] = quote.requestDeadline;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <p className="text-sm">Make sure the rest of the tokens were deposited to{" "}{t2}{t3}{" "}until the deadline {t4}</p>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== quote.depositAddress) {
    t6 = <SubmitDepositTxForm depositAddress={quote.depositAddress} />;
    $[14] = quote.depositAddress;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t1 || $[17] !== t5 || $[18] !== t6) {
    t7 = <StepItem title="Incomplete Deposit" description="Waiting for confirmation of the rest of the tokens to be deposited. Amounts are hidden on this page for security reasons." isLast={true} type="warning" {...t1}>{t5}{t6}</StepItem>;
    $[16] = t1;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function DepositCompleted(t0) {
  const $ = _c(25);
  const {
    quote,
    originChain,
    prevType,
    nextType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "success" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nextType) {
    t2 = nextType !== undefined && nextType !== "success" && {
      nextType
    };
    $[2] = nextType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== originChain || $[5] !== quote.depositAddress || $[6] !== quote.depositType) {
    t3 = <DepositAddressDisplay depositAddress={quote.depositAddress} depositType={quote.depositType} originChain={originChain} />;
    $[4] = originChain;
    $[5] = quote.depositAddress;
    $[6] = quote.depositType;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== quote.depositMemo) {
    t4 = quote.depositMemo !== undefined && quote.depositMemo !== "" && <>{" "}with memo <Short text={quote.depositMemo} length={HASH_LENGTH} /></>;
    $[8] = quote.depositMemo;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== quote) {
    t5 = <SwapStepTimestamp quote={quote} />;
    $[10] = quote;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <p className="flex flex-wrap gap-x-2 text-sm leading-7">Tokens were deposited to{" "}{t3}{t4}{t5}</p>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== originChain || $[17] !== quote.depositTransactions || $[18] !== quote.depositType) {
    t7 = quote.depositTransactions.length > 0 && <p className="flex flex-wrap gap-x-2 text-sm">Deposit {pluralize(quote.depositTransactions.length, "transaction")}:{" "}{quote.depositTransactions.map(tx => <Short key={tx.txHash} text={tx.txHash} length={HASH_LENGTH} link={getTxLink(getDepositChain(quote.depositType, originChain), tx.txHash)} />)}</p>;
    $[16] = originChain;
    $[17] = quote.depositTransactions;
    $[18] = quote.depositType;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t6 || $[23] !== t7) {
    t8 = <StepItem title="Deposit Completed" description="Token deposit has been confirmed." type="success" {...t1} {...t2}>{t6}{t7}</StepItem>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function Processing(t0) {
  const $ = _c(10);
  const {
    requestDeadline,
    quote,
    prevType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "processing" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== quote) {
    t2 = <p className="text-sm leading-7">Processing started <SwapStepTimestamp quote={quote} /></p>;
    $[2] = quote;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== requestDeadline) {
    t3 = <p className="text-sm">Wait until intent is executed and the bridging process is finalized. The deadline is <FormattedTime date={requestDeadline} />.</p>;
    $[4] = requestDeadline;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <StepItem title="Processing" description="Your transaction is being processed." isLast={true} type="processing" {...t1}>{t2}{t3}</StepItem>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function Processed(t0) {
  const $ = _c(3);
  const {
    quote
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-sm">Transaction was executed and the bridging process was successful.</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== quote) {
    t2 = <StepItem title="Processed" description="Your transaction has been processed." type="success">{t1}<SwapSteps transaction={quote} /></StepItem>;
    $[1] = quote;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function ProcessingWhenRefunded(t0) {
  const $ = _c(10);
  const {
    quote,
    prevType,
    nextType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "success" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nextType) {
    t2 = nextType !== undefined && nextType !== "success" && {
      nextType
    };
    $[2] = nextType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== quote) {
    t3 = <SwapSteps transaction={quote} />;
    $[4] = quote;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <StepItem title="Processing" description="If any of the swap steps were executed, they are listed below." type="success" {...t1} {...t2}>{t3}</StepItem>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function Success(t0) {
  const $ = _c(25);
  const {
    quote,
    destinationChain,
    recipientType
  } = t0;
  const t1 = quote.recipient;
  let t2;
  if ($[0] !== destinationChain || $[1] !== recipientType) {
    t2 = getRecipientChain(recipientType, destinationChain);
    $[0] = destinationChain;
    $[1] = recipientType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== quote.recipient || $[4] !== recipientType || $[5] !== t2) {
    t3 = <RecipientValue recipientType={recipientType} recipient={t1} chain={t2} length={HASH_LENGTH} recipientDisplayMode="details" />;
    $[3] = quote.recipient;
    $[4] = recipientType;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = recipientType === "INTENTS" && " on NEAR Intents";
  let t5;
  if ($[7] !== quote) {
    t5 = <SwapStepTimestamp quote={quote} />;
    $[7] = quote;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = <p className="flex flex-wrap gap-x-2 text-sm leading-7">Transferred to{" "}{t3}{t4}{t5}</p>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== destinationChain || $[14] !== quote || $[15] !== recipientType) {
    t7 = shouldShowWithdrawTransactions(quote) && quote.withdrawTransactions.length > 0 && <p className="flex flex-wrap gap-x-2 text-sm">Withdraw{" "}{pluralize(quote.withdrawTransactions.length, "transaction")}:{" "}{quote.withdrawTransactions.map(tx => <Short key={tx.txHash} text={tx.txHash} length={HASH_LENGTH} link={getTxLink(getRecipientChain(recipientType, destinationChain), tx.txHash)} />)}</p>;
    $[13] = destinationChain;
    $[14] = quote;
    $[15] = recipientType;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== destinationChain || $[18] !== quote.passiveWithdrawTransactions || $[19] !== recipientType) {
    t8 = quote.passiveWithdrawTransactions.length > 0 && <p className="flex flex-wrap gap-x-2 text-sm">Passive withdraw{" "}{pluralize(quote.passiveWithdrawTransactions.length, "transaction")}:{" "}{quote.passiveWithdrawTransactions.map(tx_0 => <Short key={tx_0.txHash} text={tx_0.txHash} length={HASH_LENGTH} link={getTxLink(getRecipientChain(recipientType, destinationChain), tx_0.txHash)} />)}</p>;
    $[17] = destinationChain;
    $[18] = quote.passiveWithdrawTransactions;
    $[19] = recipientType;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <StepItem title="Withdrawn" description="Funds were successfully withdrawn" isLast={true} type="success">{t6}{t7}{t8}</StepItem>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
function Failed(t0) {
  const $ = _c(9);
  const {
    quote,
    prevType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "failed" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== quote) {
    t2 = <p className="flex flex-wrap gap-x-2 text-sm leading-7">The transaction has failed to complete and deposit was not refunded<SwapStepTimestamp quote={quote} /></p>;
    t3 = <SwapSteps transaction={quote} />;
    $[2] = quote;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <StepItem title="Failed" description="Transaction failed" isLast={true} type="failed" {...t1}>{t2}{t3}</StepItem>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function Created(t0) {
  const $ = _c(10);
  const {
    quote,
    prevType,
    nextType
  } = t0;
  let t1;
  if ($[0] !== prevType) {
    t1 = prevType !== undefined && prevType !== "success" && {
      prevType
    };
    $[0] = prevType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== nextType) {
    t2 = nextType !== undefined && nextType !== "success" && {
      nextType
    };
    $[2] = nextType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== quote.createdAt) {
    t3 = <p className="text-sm">Transaction was created <FormattedTime date={quote.createdAt} /></p>;
    $[4] = quote.createdAt;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <StepItem title="Created" isFirst={true} type="success" {...t1} {...t2}>{t3}</StepItem>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function Refunded(t0) {
  const $ = _c(20);
  const {
    quote,
    prevType,
    nextType
  } = t0;
  const tokens = useTokens();
  let t1;
  if ($[0] !== quote.refundReason) {
    t1 = quote.refundReason !== null && quote.refundReason !== "" ? formatRefundReason(quote.refundReason) : null;
    $[0] = quote.refundReason;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const refundReasonFormatted = t1;
  let t2;
  if ($[2] !== nextType || $[3] !== prevType || $[4] !== quote || $[5] !== refundReasonFormatted || $[6] !== tokens) {
    const refundChain = getRefundChain(quote.refundType, tokens[quote.originAsset]?.chain);
    let t3;
    if ($[8] !== prevType) {
      t3 = prevType !== undefined && prevType !== "refunded" && {
        prevType
      };
      $[8] = prevType;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== nextType) {
      t4 = nextType !== undefined && nextType !== "refunded" && {
        nextType
      };
      $[10] = nextType;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] !== quote) {
      t5 = <p className="text-sm leading-7"><SwapStepTimestamp quote={quote} /></p>;
      $[12] = quote;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== quote.originAsset || $[15] !== quote.refundFeeFormatted || $[16] !== quote.refundType || $[17] !== quote.refundedAmountFormatted || $[18] !== quote.refundedAmountUsd) {
      t6 = (quote.refundedAmountFormatted !== "0" || quote.refundFeeFormatted !== null && quote.refundFeeFormatted !== "0") && <div className="mt-4 flex flex-wrap items-start gap-x-6 gap-y-4">{quote.refundedAmountFormatted !== "0" && <div><div className="text-muted-foreground mb-2 text-sm">Refunded amount</div><Price type={quote.refundType} price={quote.refundedAmountFormatted} length={PRICE_LENGTH} defuseAssetId={quote.originAsset} usdAmount={quote.refundedAmountUsd} /></div>}{quote.refundFeeFormatted !== null && quote.refundFeeFormatted !== "0" && <div><div className="text-muted-foreground mb-2 text-sm">Refund fee</div><Price price={quote.refundFeeFormatted} length={PRICE_LENGTH} defuseAssetId={quote.originAsset} /></div>}</div>;
      $[14] = quote.originAsset;
      $[15] = quote.refundFeeFormatted;
      $[16] = quote.refundType;
      $[17] = quote.refundedAmountFormatted;
      $[18] = quote.refundedAmountUsd;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    t2 = <StepItem title="Refunded" description={refundReasonFormatted !== null ? `Transaction refunded. Reason: ${refundReasonFormatted}` : "Transaction refunded"} isLast={true} type="refunded" {...t3} {...t4}>{t5}{t6}{quote.refundTo !== "" && <div className="text-muted-foreground mt-4 flex gap-1.5 text-sm"><span>Refunded to:</span><Short text={quote.refundTo} length={HASH_LENGTH} link={getAddressLink(refundChain, quote.refundTo)} /></div>}</StepItem>;
    $[2] = nextType;
    $[3] = prevType;
    $[4] = quote;
    $[5] = refundReasonFormatted;
    $[6] = tokens;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function TransactionPlanCardContent(t0) {
  const $ = _c(68);
  const {
    quote,
    originChain,
    destinationChain
  } = t0;
  if (quote.status === "SUCCESS") {
    let t1;
    if ($[0] !== quote) {
      t1 = <Created quote={quote} />;
      $[0] = quote;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== originChain || $[3] !== quote) {
      t2 = <DepositCompleted quote={quote} originChain={originChain} />;
      $[2] = originChain;
      $[3] = quote;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== quote) {
      t3 = <Processed quote={quote} />;
      $[5] = quote;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== destinationChain || $[8] !== quote) {
      t4 = <Success quote={quote} destinationChain={destinationChain} recipientType={quote.recipientType} />;
      $[7] = destinationChain;
      $[8] = quote;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
      t5 = <>{t1}{t2}{t3}{t4}</>;
      $[10] = t1;
      $[11] = t2;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    return t5;
  }
  if (quote.status === "FAILED") {
    let t1;
    if ($[15] !== quote) {
      t1 = <Created quote={quote} />;
      $[15] = quote;
      $[16] = t1;
    } else {
      t1 = $[16];
    }
    let t2;
    if ($[17] !== originChain || $[18] !== quote) {
      t2 = <DepositCompleted quote={quote} originChain={originChain} nextType="failed" />;
      $[17] = originChain;
      $[18] = quote;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    let t3;
    if ($[20] !== quote) {
      t3 = <Failed quote={quote} prevType="success" />;
      $[20] = quote;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    let t4;
    if ($[22] !== t1 || $[23] !== t2 || $[24] !== t3) {
      t4 = <>{t1}{t2}{t3}</>;
      $[22] = t1;
      $[23] = t2;
      $[24] = t3;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    return t4;
  }
  if (quote.status === "PENDING_DEPOSIT") {
    let t1;
    if ($[26] !== quote) {
      t1 = <Created quote={quote} nextType="pending" />;
      $[26] = quote;
      $[27] = t1;
    } else {
      t1 = $[27];
    }
    let t2;
    if ($[28] !== originChain || $[29] !== quote) {
      t2 = <PendingDeposit quote={quote} originChain={originChain} prevType="success" />;
      $[28] = originChain;
      $[29] = quote;
      $[30] = t2;
    } else {
      t2 = $[30];
    }
    let t3;
    if ($[31] !== t1 || $[32] !== t2) {
      t3 = <>{t1}{t2}</>;
      $[31] = t1;
      $[32] = t2;
      $[33] = t3;
    } else {
      t3 = $[33];
    }
    return t3;
  }
  if (quote.status === "INCOMPLETE_DEPOSIT") {
    let t1;
    if ($[34] !== quote) {
      t1 = <Created quote={quote} />;
      $[34] = quote;
      $[35] = t1;
    } else {
      t1 = $[35];
    }
    let t2;
    let t3;
    if ($[36] !== originChain || $[37] !== quote) {
      t2 = <PartialDeposit quote={quote} originChain={originChain} nextType="warning" />;
      t3 = <IncompleteDeposit quote={quote} originChain={originChain} prevType="success" />;
      $[36] = originChain;
      $[37] = quote;
      $[38] = t2;
      $[39] = t3;
    } else {
      t2 = $[38];
      t3 = $[39];
    }
    let t4;
    if ($[40] !== t1 || $[41] !== t2 || $[42] !== t3) {
      t4 = <>{t1}{t2}{t3}</>;
      $[40] = t1;
      $[41] = t2;
      $[42] = t3;
      $[43] = t4;
    } else {
      t4 = $[43];
    }
    return t4;
  }
  if (quote.status === "PROCESSING") {
    let t1;
    if ($[44] !== quote) {
      t1 = <Created quote={quote} />;
      $[44] = quote;
      $[45] = t1;
    } else {
      t1 = $[45];
    }
    let t2;
    if ($[46] !== originChain || $[47] !== quote) {
      t2 = <DepositCompleted quote={quote} originChain={originChain} nextType="processing" />;
      $[46] = originChain;
      $[47] = quote;
      $[48] = t2;
    } else {
      t2 = $[48];
    }
    let t3;
    if ($[49] !== quote) {
      t3 = <Processing requestDeadline={quote.requestDeadline} quote={quote} prevType="success" />;
      $[49] = quote;
      $[50] = t3;
    } else {
      t3 = $[50];
    }
    let t4;
    if ($[51] !== t1 || $[52] !== t2 || $[53] !== t3) {
      t4 = <>{t1}{t2}{t3}</>;
      $[51] = t1;
      $[52] = t2;
      $[53] = t3;
      $[54] = t4;
    } else {
      t4 = $[54];
    }
    return t4;
  }
  let t1;
  if ($[55] !== quote) {
    t1 = <Created quote={quote} />;
    $[55] = quote;
    $[56] = t1;
  } else {
    t1 = $[56];
  }
  let t2;
  if ($[57] !== originChain || $[58] !== quote) {
    t2 = <DepositCompleted quote={quote} originChain={originChain} />;
    $[57] = originChain;
    $[58] = quote;
    $[59] = t2;
  } else {
    t2 = $[59];
  }
  let t3;
  let t4;
  if ($[60] !== quote) {
    t3 = <ProcessingWhenRefunded quote={quote} nextType="refunded" />;
    t4 = <Refunded quote={quote} prevType="success" />;
    $[60] = quote;
    $[61] = t3;
    $[62] = t4;
  } else {
    t3 = $[61];
    t4 = $[62];
  }
  let t5;
  if ($[63] !== t1 || $[64] !== t2 || $[65] !== t3 || $[66] !== t4) {
    t5 = <>{t1}{t2}{t3}{t4}</>;
    $[63] = t1;
    $[64] = t2;
    $[65] = t3;
    $[66] = t4;
    $[67] = t5;
  } else {
    t5 = $[67];
  }
  return t5;
}
interface TransactionPlanProps {
  className?: string;
  quote: TransactionDetails;
  originChain: string | undefined;
  destinationChain: string | undefined;
}
export function TransactionPlan(t0) {
  const $ = _c(9);
  const {
    className,
    quote,
    originChain,
    destinationChain
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("border-border flex flex-col border-y p-0 md:rounded-lg md:border", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== destinationChain || $[3] !== originChain || $[4] !== quote) {
    t2 = <TransactionPlanCardContent quote={quote} originChain={originChain} destinationChain={destinationChain} />;
    $[2] = destinationChain;
    $[3] = originChain;
    $[4] = quote;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function DepositAddressDisplay(t0) {
  const $ = _c(10);
  const {
    depositAddress,
    depositType,
    originChain
  } = t0;
  let t1;
  if ($[0] !== depositAddress || $[1] !== depositType || $[2] !== originChain) {
    t1 = getAddressLink(getDepositChain(depositType, originChain), depositAddress);
    $[0] = depositAddress;
    $[1] = depositType;
    $[2] = originChain;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== depositAddress || $[5] !== t1) {
    t2 = <Short text={depositAddress} length={HASH_LENGTH} link={t1} />;
    $[4] = depositAddress;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = depositType === "INTENTS" && " on NEAR Intents";
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}